import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ExternalLink, ChevronDown, Info, ArrowRight,
  Ruler, X, Plus, Copy, FileDown, Check,
} from 'lucide-react';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import SocialShare from '../components/FlexiOssCinematic/SocialShare';
import { ReimbursementShieldIcon, SurgeonCoverageIcon, PatientCareIcon, CalculatorIcon, FaqIcon } from '../components/FlexiOssCinematic/PhilHealthIcons';
import { useTheme } from '../contexts/ThemeContext';
import { trackEvent } from '../lib/analytics';
import { HorizontalBorderStack } from '../components/editorial/EditorialLayouts';
import {
  CATEGORIES, SIZING_DISCLAIMER, HOW_STEPS, SURGEON_POINTS, PATIENT_POINTS, RVS_FAQS,
  parsePeso, fmtPeso, caseByKey,
} from '../data/rvsData';

const PORTAL = 'https://www.philhealth.gov.ph/services/acr/';

const EASE = [0.16, 1, 0.3, 1];
// Section-level reveal: opacity only (translating a full-bleed colored section would
// create moving seams between the dark blocks). Content inside animates with fade-up.
const secReveal = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, ease: EASE },
};
const headReveal = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.6, ease: EASE },
};

const ReimbursementGuidePage = () => {
  const { theme } = useTheme();
  const dark = theme !== 'light';
  const [openKey, setOpenKey] = useState(CATEGORIES[0].key);
  const [selected, setSelected] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [copiedEstimate, setCopiedEstimate] = useState(false);

  useEffect(() => {
    document.title = 'PhilHealth Subsidy, Co-Payment & RVS Guide | FlexiOss®';
    window.scrollTo(0, 0);
  }, []);

  /* Page-scoped JSON-LD: MedicalWebPage + Breadcrumb + ItemList of procedures + FAQPage. */
  useEffect(() => {
    let pos = 0;
    const items = CATEGORIES.flatMap((cat) =>
      cat.cases.map((c) => {
        pos += 1;
        return {
          '@type': 'ListItem', position: pos,
          item: {
            '@type': 'MedicalProcedure', name: c.name, procedureType: cat.sub,
            code: { '@type': 'MedicalCode', code: c.code, codingSystem: 'PhilHealth RVS' },
            description: `${c.app} Base PhilHealth Subsidy Allowance: ${c.subsidy}. As a premium biomaterial, FlexiOss® is typically settled via a separate co-payment. Product line: ${c.variants.map((v) => v.label).join(' or ')}. Recommended baseline size: ${c.size}.`,
          },
        };
      })
    );
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'MedicalWebPage', '@id': 'https://flexioss.ph/reimbursement-guide#webpage',
          name: 'PhilHealth Subsidy Allowance & RVS Reference for Procedures Using FlexiOss®',
          description: 'A 2026 patient-education reference to the Base PhilHealth Subsidy Allowance and RVS codes for orthopedic, spinal, dental/maxillofacial, and otologic procedures that use FlexiOss® — plus a Co-Payment Subsidy Estimator explaining how premium biomaterials are settled through a transparent co-payment.',
          url: 'https://flexioss.ph/reimbursement-guide', inLanguage: 'en',
          audience: { '@type': 'MedicalAudience', audienceType: 'Surgeons, Dentists, Oral-Maxillofacial Surgeons, Otologists, Hospital Billing Officers' },
          about: { '@type': 'MedicalDevice', name: 'FlexiOss' },
          significantLink: PORTAL,
          isPartOf: { '@type': 'WebSite', name: 'FlexiOss Philippines', url: 'https://flexioss.ph/' } },
        { '@type': 'BreadcrumbList', itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://flexioss.ph/' },
          { '@type': 'ListItem', position: 2, name: 'Reimbursement & RVS Guide', item: 'https://flexioss.ph/reimbursement-guide' } ] },
        { '@type': 'ItemList', name: 'PhilHealth Base Subsidy Allowances for Procedures Using FlexiOss®', numberOfItems: pos, itemListElement: items },
        { '@type': 'FAQPage', mainEntity: RVS_FAQS.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })) },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'reimbursement-jsonld';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const e = document.getElementById('reimbursement-jsonld'); if (e) e.remove(); };
  }, []);

  const greenHex = dark ? '#36d27e' : '#006837';
  const goldHex = dark ? '#FFC72C' : '#b38810';
  const cardSurface = dark ? 'border-white/10 bg-slate-900/50' : 'border-slate-200 bg-white';

  const CodeChip = ({ children }) => (
    <span className={`inline-flex items-center rounded-md border px-2.5 py-1 font-mono text-xs font-bold tracking-tight ${dark ? 'border-[#FFC72C]/25 bg-[#FFC72C]/10 text-[#FFC72C]' : 'border-[#006837]/25 bg-[#006837]/10 text-[#006837]'}`}>{children}</span>
  );

  const VariantPill = ({ v }) => (
    <Link to={v.href} data-testid={`variant-link-${v.key.toLowerCase()}`}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-all hover:gap-2"
      style={{ borderColor: `${v.color}55`, color: v.color, backgroundColor: `${v.color}14` }}>
      {v.label}<ArrowRight className="h-3 w-3" strokeWidth={2.2} />
    </Link>
  );

  const MetaBlock = ({ c }) => (
    <div className="mb-5 grid gap-5 sm:grid-cols-2">
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Product Line Variant</p>
        <div className="flex flex-wrap items-center gap-2">
          {c.variants.map((v, i) => (
            <React.Fragment key={v.key}>
              {i > 0 && <span className="text-xs font-semibold text-slate-500">or</span>}
              <VariantPill v={v} />
            </React.Fragment>
          ))}
        </div>
        {c.variantNote && <p className="mt-2 text-xs italic leading-relaxed text-slate-500">({c.variantNote})</p>}
      </div>
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recommended Baseline Size</p>
        <div className="flex items-start gap-2">
          <Ruler className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: goldHex }} strokeWidth={1.8} />
          <p className="text-sm font-medium leading-relaxed text-slate-300">{c.size}</p>
        </div>
      </div>
    </div>
  );

  const StatRow = ({ c }) => (
    <div className="overflow-hidden rounded-none border border-white/10">
      <div className={`flex flex-wrap items-end justify-between gap-3 p-5 ${dark ? 'bg-[#04291b]/60' : 'bg-[#eef7f1]'}`}>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Base PhilHealth Subsidy Allowance</p>
          <p className="mt-1 text-xs text-slate-500">Credited toward your total hospital bill</p>
        </div>
        <p className="text-2xl font-black tabular-nums" style={{ color: greenHex }}>{c.subsidy}</p>
      </div>
      <div className={`flex items-start gap-2 border-t px-5 py-3 ${dark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" style={{ color: goldHex }} strokeWidth={2} />
        <p className="text-[11px] leading-relaxed text-slate-500">As a premium, 3rd-generation biomaterial, FlexiOss® typically involves a separate institutional or out-of-pocket co-payment beyond this baseline allowance.</p>
      </div>
    </div>
  );

  /* ── Combined base subsidy allowance computation ── */
  const rows = selected.map((val) => { const f = caseByKey(val); return f ? { ...f, key: val } : null; }).filter(Boolean);
  const computed = [...rows]
    .sort((a, b) => parsePeso(b.c.subsidy) - parsePeso(a.c.subsidy))
    .map((r, i) => { const base = parsePeso(r.c.subsidy); const rate = i === 0 ? 1 : 0.5; return { ...r, base, rate, applied: base * rate }; });
  const combined = computed.reduce((s, r) => s + r.applied, 0);

  const addProcedure = (val) => {
    if (!val || selected.includes(val)) return;
    setSelected((prev) => [...prev, val]);
    const found = caseByKey(val);
    if (found) trackEvent('rvs_calc_add', { rvs_code: found.c.code, procedure: found.c.name, count: selected.length + 1 });
  };
  const removeProcedure = (val) => setSelected((prev) => prev.filter((x) => x !== val));

  const estimateText = () => {
    const when = new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
    const lines = [
      'FlexiOss® — PhilHealth Base Subsidy Allowance Estimate',
      `Generated: ${when}`,
      '',
      'Procedures (subsidy allowance applied):',
    ];
    computed.forEach((r) => {
      lines.push(`  • RVS ${r.c.code} — ${r.c.name}`);
      lines.push(`      ${r.rate === 1 ? 'Primary · 100%' : 'Additional · 50%'} → ${fmtPeso(r.applied)}${r.rate === 1 ? '' : ` (base ${fmtPeso(r.base)})`}`);
    });
    lines.push('');
    lines.push(`Combined Base Subsidy Allowance: ${fmtPeso(combined)}`);
    lines.push('');
    lines.push('Note: PhilHealth credits 100% of the highest-valued allowance and 50% of each additional one. This baseline subsidy is a credit to your hospital bill; the premium FlexiOss® biomaterial is settled separately through a transparent co-payment. Final adjudication is performed by the hospital\u2019s PhilHealth desk.');
    lines.push('Source: https://flexioss.ph/reimbursement-guide');
    return lines.join('\n');
  };

  const copyEstimate = async () => {
    const text = estimateText();
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (_) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopiedEstimate(true);
    trackEvent('calc_export', { method: 'copy', codes: computed.length, total: combined });
    setTimeout(() => setCopiedEstimate(false), 2200);
  };

  const exportEstimatePdf = () => {
    trackEvent('calc_export', { method: 'pdf', codes: computed.length, total: combined });
    const when = new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
    const rows = computed.map((r) =>
      `<tr><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-weight:700;color:#006837">RVS ${r.c.code}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb">${r.c.name}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;white-space:nowrap">${r.rate === 1 ? 'Primary · 100%' : 'Additional · 50%'}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:700">${fmtPeso(r.applied)}</td></tr>`
    ).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>FlexiOss PhilHealth Subsidy Allowance Estimate</title></head>` +
      `<body style="font-family:Arial,Helvetica,sans-serif;color:#0f172a;max-width:720px;margin:24px auto;padding:0 16px">` +
      `<div style="border-top:6px solid #006837;border-bottom:2px solid #FFC72C;padding:14px 0;margin-bottom:18px">` +
      `<h1 style="margin:0;font-size:20px;color:#006837">FlexiOss® — PhilHealth Base Subsidy Allowance Estimate</h1>` +
      `<p style="margin:4px 0 0;font-size:12px;color:#64748b">Generated ${when} · Baseline subsidy credit to the hospital bill</p></div>` +
      `<table style="width:100%;border-collapse:collapse;font-size:13px"><thead><tr style="background:#f0f9f4">` +
      `<th style="padding:8px 10px;text-align:left;border-bottom:2px solid #006837">Code</th>` +
      `<th style="padding:8px 10px;text-align:left;border-bottom:2px solid #006837">Procedure</th>` +
      `<th style="padding:8px 10px;text-align:left;border-bottom:2px solid #006837">Allowance</th>` +
      `<th style="padding:8px 10px;text-align:right;border-bottom:2px solid #006837">Applied</th></tr></thead><tbody>${rows}</tbody></table>` +
      `<div style="display:flex;justify-content:space-between;align-items:center;margin-top:16px;padding:14px 16px;background:#eef7f1;border:1px solid #006837;border-radius:10px">` +
      `<span style="font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#64748b">Combined Base Subsidy Allowance</span>` +
      `<span style="font-size:22px;font-weight:800;color:#006837">${fmtPeso(combined)}</span></div>` +
      `<p style="margin-top:16px;font-size:11px;line-height:1.6;color:#64748b">PhilHealth credits 100% of the highest-valued allowance and 50% of each additional one. This baseline subsidy is a credit toward your hospital bill — not the full cost of surgery. The premium FlexiOss® biomaterial is settled separately through a transparent institutional or out-of-pocket co-payment. Final adjudication is performed by the hospital's PhilHealth and billing desks. Source: flexioss.ph/reimbursement-guide</p>` +
      `</body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 350);
  };

  return (
    <SubpageLayout productName="Reimbursement & RVS Guide">
      {/* Hero */}
      <motion.section {...secReveal} data-testid="rvs-hero" className="relative overflow-hidden bg-slate-950">
        <div className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-[#006837]/20 blur-[120px]" />
        <div className="pointer-events-none absolute right-0 top-10 h-72 w-72 rounded-full bg-[#FFC72C]/10 blur-[120px]" />
        <div className="relative mx-auto max-w-5xl px-6 pb-14 pt-14 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mb-5 inline-flex items-center gap-2 rounded-none bg-[#006837] px-4 py-1.5 ring-1 ring-[#FFC72C]/30">
                <ReimbursementShieldIcon className="h-4 w-4 keep-white" strokeWidth={1.7} />
                <span className="text-xs font-bold uppercase tracking-[0.18em] keep-white">PhilHealth · Subsidy & RVS Reference</span>
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }} className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                PhilHealth Subsidy &amp; <span className="italic" style={{ color: greenHex }}>RVS Directory</span>
              </motion.h1>
              <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 0.7, ease: EASE, delay: 0.24 }} style={{ transformOrigin: 'left' }} className="my-5 h-1 w-40 rounded-full bg-gradient-to-r from-[#006837] to-[#FFC72C]" />
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }} className="max-w-xl text-base leading-relaxed text-slate-300">
                How your PhilHealth <strong className="text-white">subsidy allowance</strong> works for procedures that
                use FlexiOss®, plus a 2026 RVS reference for orthopedic, spinal, dental / maxillofacial, and otologic
                surgery — and how a premium biomaterial upgrade is settled through a transparent co-payment.
              </motion.p>
              <div className="mt-6"><ReadAloud dark={dark} /></div>
            </div>
            <a href={PORTAL} target="_blank" rel="noopener noreferrer" data-testid="rvs-verify-portal"
              onClick={() => trackEvent('verify_philhealth_portal', { source: 'reimbursement_guide' })}
              className={`group flex max-w-xs shrink-0 items-start gap-3 rounded-none border p-4 transition-colors ${dark ? 'border-[#006837]/45 bg-[#04291b]/60 hover:bg-[#04291b]/80' : 'border-[#006837]/25 bg-[#eef7f1] hover:bg-[#e3f1e8]'}`}>
              <ExternalLink className="mt-0.5 h-5 w-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: greenHex }} strokeWidth={1.8} />
              <span className="text-xs leading-relaxed text-slate-300">
                <strong className="text-white">Option:</strong> Verify current real-time case rates directly via the
                official <span className="font-bold underline decoration-dotted underline-offset-2" style={{ color: greenHex }}>PhilHealth Case Rate Search Portal</span>.
              </span>
            </a>
          </div>
        </div>
      </motion.section>

      {/* How reimbursement works */}
      <motion.section {...secReveal} data-testid="how-it-works" className="bg-slate-900 py-14 sm:py-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <motion.h2 {...headReveal} className="mb-3 text-2xl font-display font-bold tracking-tight text-white sm:text-3xl">How PhilHealth Reimbursement Works</motion.h2>
          <p className="mb-9 max-w-2xl text-sm leading-relaxed text-slate-400">
            PhilHealth reimburses the surgical <strong className="text-slate-300">procedure</strong>, not the brand of biomaterial.
            FlexiOss® is the synthetic graft utilized within that covered procedure.
          </p>
          <div className="grid gap-px overflow-hidden rounded-none border border-white/10 bg-white/10 sm:grid-cols-3">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} data-testid={`how-step-${i}`} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.55, ease: EASE, delay: i * 0.12 }} className="bg-slate-900 p-7">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-none bg-[#006837] text-white"><Icon className="h-5 w-5" strokeWidth={1.7} /></div>
                    <span className="font-mono text-xs font-bold" style={{ color: goldHex }}>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="mb-2 text-base font-bold text-white">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{s.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Anchored-top Clinical Sizing Disclaimer */}
      <motion.section {...secReveal} className="bg-slate-900 pb-2">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div data-testid="rvs-sizing-disclaimer" className={`flex items-start gap-4 rounded-none border-l-4 p-6 ${dark ? 'border-l-[#FFC72C] border border-white/10 bg-slate-900/60' : 'border-l-[#b38810] border border-slate-200 bg-[#fffdf6]'}`}>
            <Ruler className="mt-0.5 h-6 w-6 flex-shrink-0" style={{ color: goldHex }} strokeWidth={1.7} />
            <p className="text-sm leading-relaxed text-slate-300">
              <strong className="text-white">Clinical Sizing Disclaimer:</strong> {SIZING_DISCLAIMER}
            </p>
          </div>
        </div>
      </motion.section>

      {/* Health literacy — premium technology & co-payments */}
      <motion.section {...secReveal} data-testid="rvs-copay-education" className="bg-slate-900 pb-2 pt-10">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className={`relative overflow-hidden rounded-none border p-7 sm:p-10 ${dark ? 'border-[#FFC72C]/25 bg-gradient-to-br from-[#0c0f0a] to-slate-900' : 'border-[#FFC72C]/35 bg-gradient-to-br from-white to-[#fffdf5]'}`}>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FFC72C] via-[#006837]/60 to-transparent" />
            <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#FFC72C]/10 blur-3xl" />
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: goldHex }}>Patient Health Literacy</p>
            <motion.h2 {...headReveal} className="mb-6 text-2xl font-display font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">Understanding Premium Technology &amp; Co-Payments</motion.h2>
            <div className="max-w-3xl space-y-5 text-sm leading-relaxed text-slate-300 sm:text-base">
              <p>Standard healthcare allocations are engineered around standard, legacy materials. However, achieving optimal, long-term surgical success often requires advanced clinical technology. FlexiOss® is a premium, 3rd-generation biphasic biomaterial matrix clinically proven to accelerate bone healing kinetics by up to 50% while preventing graft migration.</p>
              <p>In private medical centers, your PhilHealth Case Rate functions as a <strong className="text-white">baseline subsidy allowance</strong>—a credit applied directly to your total hospital bill. Because FlexiOss® represents an advanced, specialized tier of bio-engineering, patients routinely navigate a standard co-payment or out-of-pocket structure to cover the premium material cost. Investing in premium biomaterials intraoperatively significantly reduces the long-term risk of structural non-unions, graft rejection, and costly secondary revision surgeries.</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Directory — accordion blocks */}
      <motion.section {...secReveal} data-testid="rvs-directory" className="bg-slate-900 py-12 sm:py-14">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <motion.h2 {...headReveal} className="mb-8 text-2xl font-display font-bold tracking-tight text-white sm:text-3xl">2026 RVS &amp; Base Subsidy Allowance Directory</motion.h2>
          <div className="space-y-5">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isOpen = openKey === cat.key;
              return (
                <div key={cat.key} data-testid={`rvs-block-${cat.key}`} className={`overflow-hidden rounded-none border ${cardSurface} ${dark ? 'shadow-[0_0_44px_-22px_rgba(0,104,55,0.5)]' : 'shadow-[0_10px_34px_-16px_rgba(0,104,55,0.14)]'}`}>
                  <button
                    onClick={() => { const next = isOpen ? '' : cat.key; setOpenKey(next); if (next) trackEvent('rvs_block_open', { block: cat.key, label: cat.label }); }}
                    data-testid={`rvs-toggle-${cat.key}`} aria-expanded={isOpen}
                    className={`flex w-full items-center gap-4 border-l-4 px-5 py-5 text-left transition-colors sm:px-7 ${dark ? 'border-[#006837] hover:bg-white/[0.02]' : 'border-[#006837] hover:bg-slate-50'}`}>
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-none bg-[#006837] text-white"><Icon className="h-5 w-5" strokeWidth={1.6} /></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold tracking-tight text-white sm:text-lg">{cat.label}</h3>
                      <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{cat.sub} · {cat.cases.length} {cat.cases.length === 1 ? 'code' : 'codes'}</p>
                    </div>
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{ color: isOpen ? greenHex : undefined }} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.32, ease: 'easeInOut' }} className="overflow-hidden">
                        <div className="px-5 pb-2 sm:px-7">
                          {cat.cases.map((c) => (
                            <div key={c.code} data-testid={`rvs-case-${c.code}`} className="border-t border-white/10 py-6">
                              <div className="mb-4 flex flex-wrap items-center gap-3">
                                <CodeChip>RVS {c.code}</CodeChip>
                                <h4 className="text-sm font-bold leading-snug text-white sm:text-base">{c.name}</h4>
                              </div>
                              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Clinical Application</p>
                              <p className="mb-5 max-w-3xl text-sm leading-relaxed text-slate-400">{c.app}</p>
                              <MetaBlock c={c} />
                              <StatRow c={c} />
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Combined Second Case Rate Calculator */}
      <motion.section {...secReveal} data-testid="rvs-calculator-section" className="bg-slate-950 py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className={`mb-3 inline-flex items-center gap-2 rounded-none border px-4 py-1.5 ${dark ? 'border-[#006837]/45 bg-[#006837]/10' : 'border-[#006837]/25 bg-[#006837]/[0.06]'}`}>
            <CalculatorIcon className="h-4 w-4" style={{ color: greenHex }} strokeWidth={1.7} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: greenHex }}>Interactive Tool</span>
          </div>
          <motion.h2 {...headReveal} className="mb-3 text-2xl font-display font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">Co-Payment Subsidy Estimator</motion.h2>
          <p className="mb-8 max-w-2xl text-sm leading-relaxed text-slate-400">
            Add the procedures planned for your session to estimate the <strong className="text-slate-300">baseline
            subsidy allowance</strong> PhilHealth credits to your hospital bill. With multiple procedures, PhilHealth
            credits <strong className="text-slate-300">100%</strong> of the highest-valued allowance and{' '}
            <strong className="text-slate-300">50%</strong> of each additional one. A premium biomaterial like FlexiOss®
            is typically settled through a separate co-payment.
          </p>
          <div data-testid="rvs-calculator" className={`rounded-none border p-7 sm:p-9 ${cardSurface} ${dark ? 'shadow-[0_0_44px_-20px_rgba(0,104,55,0.45)]' : 'shadow-[0_10px_34px_-14px_rgba(0,104,55,0.14)]'}`}>
            <label htmlFor="rvs-calc-add" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Add a procedure</label>
            <div className="relative mt-3">
              <select id="rvs-calc-add" data-testid="rvs-calc-add" value="" onChange={(e) => addProcedure(e.target.value)}
                className={`w-full appearance-none rounded-none border px-4 py-3.5 pr-11 text-sm font-medium text-white outline-none transition-colors focus:border-[#006837] ${dark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
                <option value="">Select an RVS code to add…</option>
                {CATEGORIES.map((cat) => (
                  <optgroup key={cat.key} label={cat.label}>
                    {cat.cases.map((c, i) => (
                      <option key={c.code} value={`${cat.key}::${i}`} disabled={selected.includes(`${cat.key}::${i}`)}>
                        RVS {c.code} — {c.name}{selected.includes(`${cat.key}::${i}`) ? ' (added)' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <Plus className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: greenHex }} strokeWidth={2} />
            </div>

            {computed.length === 0 ? (
              <p className="mt-6 text-sm leading-relaxed text-slate-400" data-testid="rvs-calc-empty">
                Add one or more procedures above. With a single code you see its base subsidy allowance; add more to
                simulate a bundled session, with the 50% adjustment for each additional procedure applied automatically.
              </p>
            ) : (
              <div className="mt-7" data-testid="rvs-calc-result">
                <div className="space-y-px overflow-hidden rounded-none border border-white/10 bg-white/10">
                  {computed.map((r) => {
                    const primary = r.rate === 1;
                    return (
                      <div key={r.key} data-testid={`rvs-calc-item-${r.c.code}`} className="flex flex-wrap items-center justify-between gap-3 bg-slate-900 p-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <CodeChip>RVS {r.c.code}</CodeChip>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-white">{r.c.name}</p>
                            <span className="mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: primary ? greenHex : goldHex, backgroundColor: `${primary ? greenHex : goldHex}1f` }}>
                              {primary ? 'Primary · 100%' : 'Additional · 50%'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-black tabular-nums text-white">{fmtPeso(r.applied)}</p>
                            {!primary && <p className="text-[10px] tabular-nums text-slate-500 line-through">{fmtPeso(r.base)}</p>}
                          </div>
                          <button onClick={() => removeProcedure(r.key)} data-testid={`rvs-calc-remove-${r.c.code}`} aria-label={`Remove RVS ${r.c.code}`} className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                            <X className="h-4 w-4" strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div data-testid="rvs-calc-total" className={`mt-5 flex flex-wrap items-end justify-between gap-3 rounded-none border p-6 ${dark ? 'border-[#006837]/45 bg-[#04291b]/60' : 'border-[#006837]/25 bg-[#eef7f1]'}`}>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Combined Base Subsidy Allowance</p>
                    <p className="mt-1 text-xs text-slate-500">PhilHealth credit toward your bill ({computed.length} {computed.length === 1 ? 'procedure' : 'procedures'})</p>
                  </div>
                  <p className="text-3xl font-black tabular-nums" style={{ color: greenHex }}>{fmtPeso(combined)}</p>
                </div>
                <div data-testid="rvs-calc-copay-note" className={`mt-3 flex items-start gap-2 rounded-none border border-dashed p-4 ${dark ? 'border-[#FFC72C]/30 bg-[#FFC72C]/[0.05]' : 'border-[#b38810]/30 bg-[#fffdf5]'}`}>
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: goldHex }} strokeWidth={2} />
                  <p className="text-xs leading-relaxed text-slate-400">
                    This figure is the <strong className="text-slate-300">baseline subsidy</strong> PhilHealth applies to your bill — not the full cost of surgery. The premium FlexiOss® biomaterial is settled separately through a transparent institutional or out-of-pocket <strong className="text-slate-300">co-payment</strong>. For your exact co-payment, please ask your hospital's PhilHealth and billing desks for assistance — they handle this for you.
                  </p>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button onClick={copyEstimate} data-testid="rvs-calc-copy" className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${dark ? 'border-[#006837]/50 bg-[#006837]/10 text-[#36d27e] hover:bg-[#006837]/20' : 'border-[#006837]/30 bg-[#006837]/[0.06] text-[#006837] hover:bg-[#006837]/[0.12]'}`}>
                    {copiedEstimate ? <Check className="h-4 w-4" strokeWidth={2.4} /> : <Copy className="h-4 w-4" strokeWidth={2} />}
                    {copiedEstimate ? 'Copied!' : 'Copy estimate'}
                  </button>
                  <button onClick={exportEstimatePdf} data-testid="rvs-calc-pdf" className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${dark ? 'border-white/15 bg-white/5 text-white hover:bg-white/10' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}>
                    <FileDown className="h-4 w-4" strokeWidth={2} /> Download PDF
                  </button>
                  <button onClick={() => setSelected([])} data-testid="rvs-calc-clear" className="ml-auto text-xs font-semibold uppercase tracking-wide text-slate-500 transition-colors hover:text-slate-300">Clear all</button>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-slate-500">
                  PhilHealth typically applies a maximum of two subsidy allowances per single confinement; this is a
                  planning estimate. Final adjudication and your co-payment are confirmed by the hospital's PhilHealth and billing desks.
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* For Surgeons / Patients */}
      <motion.section {...secReveal} className="bg-slate-900 py-14 sm:py-16">
        <div className="mx-auto grid max-w-5xl gap-6 px-6 md:grid-cols-2 lg:px-8">
          <motion.div data-testid="for-surgeons" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.6, ease: EASE }} className={`rounded-none border p-7 ${cardSurface}`}>
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-none bg-[#006837] text-white"><SurgeonCoverageIcon className="h-5 w-5" strokeWidth={1.7} /></div>
              <h3 className="text-xl font-display font-bold tracking-tight text-white">For Surgeons</h3>
            </div>
            <HorizontalBorderStack items={SURGEON_POINTS} accent={greenHex} testid="surgeon-points" />
          </motion.div>
          <motion.div data-testid="for-patients" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.6, ease: EASE, delay: 0.12 }} className={`rounded-none border p-7 ${cardSurface}`}>
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-none bg-[#FFC72C] text-[#3d2e00]"><PatientCareIcon className="h-5 w-5" strokeWidth={1.7} accent="#04291b" /></div>
              <h3 className="text-xl font-display font-bold tracking-tight text-white">For Patients</h3>
            </div>
            <HorizontalBorderStack items={PATIENT_POINTS} accent={goldHex} testid="patient-points" />
          </motion.div>
        </div>
      </motion.section>

      {/* Operational note + regulatory note */}
      <motion.section {...secReveal} className="bg-slate-950 py-14 sm:py-16">
        <div className="mx-auto max-w-5xl space-y-6 px-6 lg:px-8">
          <div data-testid="rvs-operational-note" className={`rounded-none border p-7 ${cardSurface}`}>
            <div className="mb-3 flex items-center gap-3">
              <Info className="h-5 w-5" style={{ color: greenHex }} strokeWidth={1.7} />
              <h3 className="text-base font-bold tracking-tight text-white">Operational Note on Combined Procedures</h3>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              When a bone-graft application is performed adjunctively alongside a primary stabilizing framework (such as
              an ORIF plate fixation), PhilHealth credits <strong className="text-white">100%</strong> of the
              highest-valued subsidy allowance, while the secondary bone-graft component
              (<span className="font-mono text-xs font-bold" style={{ color: goldHex }}>RVS 20900 / 20902</span>) can be
              concurrently appended at <strong className="text-white">50%</strong> of its designated subsidy allowance.
            </p>
          </div>
          <div data-testid="rvs-sizing-reassertion" className={`rounded-none border-l-4 p-7 ${dark ? 'border-l-[#FFC72C] border border-white/10 bg-slate-900/60' : 'border-l-[#b38810] border border-slate-200 bg-[#fffdf6]'}`}>
            <div className="mb-3 flex items-center gap-3">
              <Ruler className="h-5 w-5" style={{ color: goldHex }} strokeWidth={1.7} />
              <h3 className="text-base font-bold tracking-tight text-white">Regulatory Note on Dimensions</h3>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Variances in pathological presentation, structural degradation, and target defect boundaries mean that
              actual dimensional consumption will fluctuate. The final determination of required biomaterial dimensions
              remains strictly under the jurisdiction of the operating surgeon.
            </p>
          </div>
        </div>
      </motion.section>

      {/* FAQ */}
      <motion.section {...secReveal} data-testid="rvs-faq" className="bg-slate-900 py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className={`mb-3 inline-flex items-center gap-2 rounded-none border px-4 py-1.5 ${dark ? 'border-[#006837]/45 bg-[#006837]/10' : 'border-[#006837]/25 bg-[#006837]/[0.06]'}`}>
            <FaqIcon className="h-4 w-4" style={{ color: greenHex }} strokeWidth={1.7} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: greenHex }}>Frequently Asked</span>
          </div>
          <motion.h2 {...headReveal} className="mb-8 text-2xl font-display font-bold tracking-tight text-white sm:text-3xl">Reimbursement Questions</motion.h2>
          <div className={`overflow-hidden rounded-none border ${cardSurface}`}>
            {RVS_FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} data-testid={`faq-item-${i}`} className={i > 0 ? 'border-t border-white/10' : ''}>
                  <button onClick={() => setOpenFaq(open ? -1 : i)} data-testid={`faq-toggle-${i}`} aria-expanded={open}
                    className={`flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors ${dark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}>
                    <span className="text-sm font-bold text-white sm:text-base">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} style={{ color: open ? greenHex : undefined }} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }} className="overflow-hidden">
                        <p className="px-6 pb-6 text-sm leading-relaxed text-slate-400">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Share */}
      <motion.section {...secReveal} className="bg-slate-950 pb-4 pt-2">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <SocialShare pageKey="rvs" accent={greenHex} />
        </div>
      </motion.section>

      {/* Disclaimer */}
      <motion.section {...secReveal} className="bg-slate-950 pb-20 pt-2">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="border-t border-white/10 pt-10">
            <div data-testid="rvs-disclaimer" className={`rounded-none border p-6 ${dark ? 'border-white/10 bg-slate-900/50' : 'border-slate-200 bg-white'}`}>
              <p className="text-xs leading-relaxed text-slate-500">
                <strong className="text-slate-400">Disclaimer:</strong> The subsidy allowances and RVS codes listed on
                this page are provided as a general patient-education and reimbursement-mapping resource. Subsidy
                allocations and approval rules are subject to change without prior notice via new PhilHealth Memoranda,
                Circulars, or directives. The premium FlexiOss® biomaterial is settled through a separate institutional
                or out-of-pocket co-payment; final coverage and co-payment values are determined solely by PhilHealth
                and the hospital upon formal claim adjudication. Veterinary procedures (FlexiOss® Vet) are entirely
                ineligible for human PhilHealth subsidy allowances and are intentionally excluded from this directory.
              </p>
            </div>
          </div>
        </div>
      </motion.section>
    </SubpageLayout>
  );
};

export default ReimbursementGuidePage;
